import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Button, Input, Textarea, useToast } from '@artosera/ui';
import {
  lancerPurge,
  remettreOperationsAZero,
  reprendrePurgeSupervisee,
  resilierGalerie,
  verifierHistoriqueDepots,
} from '../../lib/api.js';
import { formaterDate } from '../../lib/format.js';
import { usePeut } from '../../auth/usePeut.js';
import { useElevation } from '../../components/useElevation.js';
import type { GalerieFiche } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import formStyles from '../../styles/form.module.css';

export function ResiliationTab({
  idGalerie,
  galerie,
}: {
  idGalerie: string;
  galerie: GalerieFiche;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const peutResilier = peut('console:terminate-gallery');
  const queryClient = useQueryClient();
  const elevation = useElevation();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState('');

  const mutation = useMutation({
    mutationFn: () => elevation.executer(() => resilierGalerie(idGalerie, confirmation.trim())),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] });
      await queryClient.invalidateQueries({ queryKey: ['console', 'galeries'] });
      navigate(`/galeries/${idGalerie}/identite`);
    },
  });

  const dejaResiliee =
    galerie.etat === 'resiliee' || galerie.etat === 'purge_en_cours' || galerie.etat === 'purgee';
  const confirmationValide = confirmation.trim() === galerie.slug;

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (confirmationValide) mutation.mutate();
  };

  if (dejaResiliee) {
    return <PurgeManuelle idGalerie={idGalerie} galerie={galerie} />;
  }

  return (
    <div className={pageStyles.panneau}>
      <p className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
        {t('fiche.resiliation.avertissement')}
      </p>

      {!peutResilier ? null : (
        <form onSubmit={onSubmit}>
          <div className={formStyles.champ}>
            <label htmlFor="resiliation-confirmation">
              {t('fiche.resiliation.champConfirmation')}
            </label>
            <Input
              id="resiliation-confirmation"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              aria-describedby="resiliation-aide"
            />
            <p id="resiliation-aide" className={formStyles.aide}>
              {t('fiche.resiliation.aide', { slug: galerie.slug })}
            </p>
          </div>
          <div className={formStyles.actionsFormulaire}>
            <Button
              type="submit"
              variant="danger"
              disabled={!confirmationValide}
              loading={mutation.isPending}
            >
              {mutation.isPending ? t('fiche.resiliation.enCours') : t('fiche.resiliation.bouton')}
            </Button>
          </div>
        </form>
      )}
      {elevation.dialogue}
    </div>
  );
}

/**
 * La purge d'une galerie résiliée : où elle en est, et son lancement MANUEL,
 * réservé à la direction de Venio (relecture ARTO-54, 2 n° 2). Sans accusé de
 * réception de l'export final, le lancement exige un motif écrit.
 */
function PurgeManuelle({
  idGalerie,
  galerie,
}: {
  idGalerie: string;
  galerie: GalerieFiche;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const queryClient = useQueryClient();
  const elevation = useElevation();
  const { toast } = useToast();
  const [confirmation, setConfirmation] = useState('');
  const [motif, setMotif] = useState('');
  // L'instant de l'affichage, figé : le rendu reste pur.
  const [maintenant] = useState(() => Date.now());
  const purge = galerie.purge;
  const enPurge = galerie.etat === 'purge_en_cours';
  const delaiEcoule =
    purge?.possibleLe !== null &&
    purge?.possibleLe !== undefined &&
    new Date(purge.possibleLe).getTime() <= maintenant;
  // Le motif n'est demandé que si rien d'autre n'autorise la purge.
  const motifRequis = !enPurge && purge?.accuseLe === null && purge.forcee === false;

  const lancement = useMutation({
    mutationFn: () =>
      elevation.executer(() =>
        lancerPurge(idGalerie, confirmation.trim(), motifRequis ? motif.trim() : undefined),
      ),
    onSuccess: async () => {
      toast({
        title: t('fiche.resiliation.lancerSucces'),
        closeLabel: t('fiche.domaines.fermer'),
      });
      await queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] });
    },
  });

  const valide =
    confirmation.trim() === galerie.slug &&
    (enPurge || delaiEcoule) &&
    (!motifRequis || motif.trim().length >= 15);
  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (valide) lancement.mutate();
  };

  return (
    <div className={pageStyles.panneau}>
      <p className={pageStyles.videEtat}>{t('fiche.resiliation.dejaResiliee')}</p>
      <div className={pageStyles.sectionTitre}>
        <h2>{t('fiche.resiliation.purgeTitre')}</h2>
      </div>
      {purge?.resilieeLe ? (
        <p>{t('fiche.resiliation.resilieeLe', { date: formaterDate(purge.resilieeLe, '—') })}</p>
      ) : null}
      {purge?.purgeeLe ? (
        <>
          <p>{t('fiche.resiliation.purgeeLe', { date: formaterDate(purge.purgeeLe, '—') })}</p>
          <p>
            {purge.effacementCompletLe
              ? t('fiche.resiliation.effacementComplet', {
                  date: formaterDate(purge.effacementCompletLe, '—'),
                })
              : t('fiche.resiliation.effacementPrevu', {
                  date: formaterDate(purge.effacementCompletPrevuLe, '—'),
                })}
          </p>
        </>
      ) : purge?.debuteLe ? (
        <p>{t('fiche.resiliation.purgeEnCours', { date: formaterDate(purge.debuteLe, '—') })}</p>
      ) : purge?.possibleLe ? (
        <p>{t('fiche.resiliation.purgePossible', { date: formaterDate(purge.possibleLe, '—') })}</p>
      ) : null}
      {purge !== null && purge.purgeeLe === null ? (
        <p>
          {purge.accuseLe !== null
            ? t('fiche.resiliation.accuseLe', { date: formaterDate(purge.accuseLe, '—') })
            : t('fiche.resiliation.sansAccuse')}
        </p>
      ) : null}
      {purge?.demandeeLe ? (
        <p>
          {t('fiche.resiliation.demandeeLe', {
            date: formaterDate(purge.demandeeLe, '—'),
            par: purge.demandeePar ?? '—',
          })}
        </p>
      ) : null}
      {purge?.forcee ? (
        <p className={pageStyles.banniere}>
          {t('fiche.resiliation.purgeForcee', { motif: purge.motif ?? '—' })}
        </p>
      ) : null}

      {purge !== null && purge.purgeeLe === null && purge.operationsEnCours > 0 ? (
        <RemiseAZero idGalerie={idGalerie} operationsEnCours={purge.operationsEnCours} />
      ) : null}

      {purge?.repriseSuperviseeRequise ? (
        <RepriseSupervisee
          idGalerie={idGalerie}
          slug={galerie.slug}
          raison={purge.repriseSuperviseeRequise.raison}
        />
      ) : null}

      {purge?.historiqueDepotsInconnu === true ? (
        <HistoriqueDepots idGalerie={idGalerie} slug={galerie.slug} />
      ) : null}

      {purge !== null &&
      purge.purgeeLe === null &&
      !purge.repriseSuperviseeRequise &&
      peut('console:terminate-gallery') ? (
        <form onSubmit={onSubmit}>
          <h3>{t('fiche.resiliation.lancerTitre')}</h3>
          <p className={formStyles.aide}>{t('fiche.resiliation.lancerTexte')}</p>
          {!enPurge && !delaiEcoule ? (
            <p className={formStyles.aide}>
              {t('fiche.resiliation.delaiEnCours', {
                date: formaterDate(purge.possibleLe, '—'),
              })}
            </p>
          ) : null}
          {motifRequis ? (
            <div className={formStyles.champ}>
              <label htmlFor="purge-motif">{t('fiche.resiliation.champMotif')}</label>
              <Input
                id="purge-motif"
                value={motif}
                onChange={(event) => setMotif(event.target.value)}
                aria-describedby="purge-motif-aide"
              />
              <p id="purge-motif-aide" className={formStyles.aide}>
                {t('fiche.resiliation.forcerTexte')} {t('fiche.resiliation.aideMotif')}
              </p>
            </div>
          ) : null}
          <div className={formStyles.champ}>
            <label htmlFor="purge-confirmation">{t('fiche.resiliation.champConfirmation')}</label>
            <Input
              id="purge-confirmation"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </div>
          <div className={formStyles.actionsFormulaire}>
            <Button type="submit" variant="danger" disabled={!valide} loading={lancement.isPending}>
              {enPurge
                ? t('fiche.resiliation.reprendreBouton')
                : t('fiche.resiliation.lancerBouton')}
            </Button>
          </div>
        </form>
      ) : null}
      {elevation.dialogue}
    </div>
  );
}

/**
 * Le compteur d'opérations de la galerie (relecture ARTO-54 4, R2) : la purge
 * attend qu'il retombe à zéro. Une panne peut le laisser bloqué ; la
 * direction de Venio le remet alors à zéro, motif écrit.
 */
function RemiseAZero({
  idGalerie,
  operationsEnCours,
}: {
  idGalerie: string;
  operationsEnCours: number;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const queryClient = useQueryClient();
  const elevation = useElevation();
  const { toast } = useToast();
  const [motif, setMotif] = useState('');
  const remise = useMutation({
    mutationFn: () => elevation.executer(() => remettreOperationsAZero(idGalerie, motif.trim())),
    onSuccess: async () => {
      toast({ title: t('fiche.resiliation.remiseSucces'), closeLabel: t('fiche.domaines.fermer') });
      setMotif('');
      await queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] });
    },
  });
  const valide = motif.trim().length >= 15;
  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (valide) remise.mutate();
  };
  return (
    <>
      <p className={pageStyles.banniere}>
        {t('fiche.resiliation.operationsEnCours', { count: operationsEnCours })}
      </p>
      {peut('console:terminate-gallery') ? (
        <form onSubmit={onSubmit}>
          <h3>{t('fiche.resiliation.remiseTitre')}</h3>
          <p className={formStyles.aide}>{t('fiche.resiliation.remiseTexte')}</p>
          <div className={formStyles.champ}>
            <label htmlFor="remise-motif">{t('fiche.resiliation.champMotifRemise')}</label>
            <Input
              id="remise-motif"
              value={motif}
              onChange={(event) => setMotif(event.target.value)}
              aria-describedby="remise-motif-aide"
            />
            <p id="remise-motif-aide" className={formStyles.aide}>
              {t('fiche.resiliation.aideMotif')}
            </p>
          </div>
          <div className={formStyles.actionsFormulaire}>
            <Button type="submit" variant="danger" disabled={!valide} loading={remise.isPending}>
              {t('fiche.resiliation.remiseBouton')}
            </Button>
          </div>
        </form>
      ) : null}
      {elevation.dialogue}
    </>
  );
}

/**
 * Reprise SUPERVISÉE d'une purge commencée sous un protocole antérieur
 * (relecture ARTO-54 6, B1) : la barrière est restée posée, et rien ne dit ce
 * qui a déjà été détruit. Réservée à la direction de Venio : avertissement,
 * motif écrit, confirmation par le slug.
 */
function RepriseSupervisee({
  idGalerie,
  slug,
  raison,
}: {
  idGalerie: string;
  slug: string;
  raison: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const queryClient = useQueryClient();
  const elevation = useElevation();
  const { toast } = useToast();
  const [motif, setMotif] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const reprise = useMutation({
    mutationFn: () =>
      elevation.executer(() =>
        reprendrePurgeSupervisee(idGalerie, confirmation.trim(), motif.trim()),
      ),
    onSuccess: async () => {
      toast({
        title: t('fiche.resiliation.repriseSucces'),
        closeLabel: t('fiche.domaines.fermer'),
      });
      await queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] });
    },
  });
  const valide = motif.trim().length >= 15 && confirmation.trim() === slug;
  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (valide) reprise.mutate();
  };
  return (
    <>
      <p className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`} role="alert">
        {t('fiche.resiliation.repriseRequise', { raison })}
      </p>
      {peut('console:terminate-gallery') ? (
        <form onSubmit={onSubmit}>
          <h3>{t('fiche.resiliation.repriseTitre')}</h3>
          <p className={formStyles.aide}>{t('fiche.resiliation.repriseTexte')}</p>
          <div className={formStyles.champ}>
            <label htmlFor="reprise-motif">{t('fiche.resiliation.champMotifReprise')}</label>
            <Input
              id="reprise-motif"
              value={motif}
              onChange={(event) => setMotif(event.target.value)}
              aria-describedby="reprise-motif-aide"
            />
            <p id="reprise-motif-aide" className={formStyles.aide}>
              {t('fiche.resiliation.aideMotif')}
            </p>
          </div>
          <div className={formStyles.champ}>
            <label htmlFor="reprise-confirmation">{t('fiche.resiliation.champConfirmation')}</label>
            <Input
              id="reprise-confirmation"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </div>
          <div className={formStyles.actionsFormulaire}>
            <Button type="submit" variant="danger" disabled={!valide} loading={reprise.isPending}>
              {t('fiche.resiliation.repriseBouton')}
            </Button>
          </div>
        </form>
      ) : null}
      {elevation.dialogue}
    </>
  );
}

/** Les dépôts saisis, un par ligne, sans doublon ni ligne vide. */
export function lireDepots(saisie: string): string[] {
  return [
    ...new Set(
      saisie
        .split(/\r?\n/)
        .map((ligne) => ligne.trim())
        .filter((ligne) => ligne.length > 0),
    ),
  ];
}

/**
 * Historique des dépôts de sauvegarde INCONNU (relecture ARTO-54 6, B2) : pas
 * de constat d'effacement complet tant que la direction de Venio ne l'a pas
 * vérifié. Liste EXHAUSTIVE des dépôts, motif écrit, confirmation par le slug.
 */
function HistoriqueDepots({ idGalerie, slug }: { idGalerie: string; slug: string }): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const queryClient = useQueryClient();
  const elevation = useElevation();
  const { toast } = useToast();
  const [saisie, setSaisie] = useState('');
  const [motif, setMotif] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const depots = lireDepots(saisie);
  const verification = useMutation({
    mutationFn: () =>
      elevation.executer(() => verifierHistoriqueDepots(idGalerie, depots, motif.trim())),
    onSuccess: async () => {
      toast({
        title: t('fiche.resiliation.historiqueSucces'),
        closeLabel: t('fiche.domaines.fermer'),
      });
      await queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] });
    },
  });
  const valide = motif.trim().length >= 15 && confirmation.trim() === slug;
  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (valide) verification.mutate();
  };
  return (
    <>
      <p className={pageStyles.banniere}>{t('fiche.resiliation.historiqueInconnu')}</p>
      {peut('console:terminate-gallery') ? (
        <form onSubmit={onSubmit}>
          <h3>{t('fiche.resiliation.historiqueTitre')}</h3>
          <p className={formStyles.aide}>{t('fiche.resiliation.historiqueTexte')}</p>
          <div className={formStyles.champ}>
            <label htmlFor="historique-depots">{t('fiche.resiliation.champDepots')}</label>
            <Textarea
              id="historique-depots"
              rows={4}
              value={saisie}
              onChange={(event) => setSaisie(event.target.value)}
              aria-describedby="historique-depots-aide"
            />
            <p id="historique-depots-aide" className={formStyles.aide}>
              {t('fiche.resiliation.aideDepots', { count: depots.length })}
            </p>
          </div>
          <div className={formStyles.champ}>
            <label htmlFor="historique-motif">{t('fiche.resiliation.champMotifHistorique')}</label>
            <Input
              id="historique-motif"
              value={motif}
              onChange={(event) => setMotif(event.target.value)}
            />
            <p className={formStyles.aide}>{t('fiche.resiliation.aideMotif')}</p>
          </div>
          <div className={formStyles.champ}>
            <label htmlFor="historique-confirmation">
              {t('fiche.resiliation.champConfirmation')}
            </label>
            <Input
              id="historique-confirmation"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </div>
          <div className={formStyles.actionsFormulaire}>
            <Button type="submit" disabled={!valide} loading={verification.isPending}>
              {t('fiche.resiliation.historiqueBouton')}
            </Button>
          </div>
        </form>
      ) : null}
      {elevation.dialogue}
    </>
  );
}
